// IGK-2P v2.1 Phase-3 — shared frontend API helpers (preview-only).
// All endpoints are IGK2P_ENABLED-gated on the backend; the UI is additionally
// hidden unless REACT_APP_IGK2P_ENABLED === 'true'.
import axios from 'axios';

export const IGK2P_ON = process.env.REACT_APP_IGK2P_ENABLED === 'true';
const API = () => `${window.__API_BASE}/api`;

// ── Blueprint (Step 1/2) ──────────────────────────────────────────────────
export const getBlueprints = (projectId) =>
  axios.get(`${API()}/projects/${projectId}/blueprints-igk2p`).then((r) => r.data);

export const genBlueprintsBatch = (projectId, shotIds) =>
  axios.post(`${API()}/projects/${projectId}/generate-blueprints-igk2p`, shotIds ? { shot_ids: shotIds } : {}).then((r) => r.data);

export const genBlueprintShot = (shotId) =>
  axios.post(`${API()}/shots/${shotId}/generate-blueprint-igk2p`, {}).then((r) => r.data);

export const approveBlueprint = (shotId, approvedBy = 'cd') =>
  axios.post(`${API()}/shots/${shotId}/approve-blueprint-igk2p`, { approved_by: approvedBy }).then((r) => r.data);

// Re-sketch iteration — feeds a text instruction to the async sketch iterator.
export const iterateSketch = (shotId, feedback) =>
  axios.post(`${API()}/shots/${shotId}/sketch-feedback`, { feedback }).then((r) => r.data);

// ── Compose / regen (Step 3) ──────────────────────────────────────────────
export const regenIGK2P = (shotId, opts = {}) =>
  axios.post(`${API()}/shots/${shotId}/regenerate-keyframe-igk2p`, { force: true, ...opts }).then((r) => r.data);

// ── Verdict / feedback audit (bench composer) ─────────────────────────────
export const addVerdictNote = (shotId, payload) =>
  axios.post(`${API()}/shots/${shotId}/igk2p/verdict-note`, payload).then((r) => r.data);
export const getVerdictHistory = (shotId) =>
  axios.get(`${API()}/shots/${shotId}/igk2p/verdict-history`).then((r) => r.data);

// ── Swap picker + binder honesty (B-block) ────────────────────────────────
export const getBinderHealth = (pid) =>
  axios.get(`${API()}/projects/${pid}/binder-health`).then((r) => r.data);
export const getBibleCards = (pid) =>
  axios.get(`${API()}/projects/${pid}/bible-cards`).then((r) => r.data);
export const getSwapBlastRadius = (pid, params) =>
  axios.get(`${API()}/projects/${pid}/swap-blast-radius`, { params }).then((r) => r.data);
export const swapBinding = (shotId, payload) =>
  axios.post(`${API()}/shots/${shotId}/swap-binding`, payload).then((r) => r.data);
export const unbindCast = (shotId, payload) =>
  axios.post(`${API()}/shots/${shotId}/unbind-cast`, payload).then((r) => r.data);

// ── Lock (Step 10) ────────────────────────────────────────────────────────
export const getVideoReadUrl = (shotId) =>
  axios.get(`${API()}/shots/${shotId}/video-read-url-igk2p`).then((r) => r.data);

export const lockShot = (shotId, by = 'cd') =>
  axios.post(`${API()}/shots/${shotId}/lock-igk2p`, { by }).then((r) => r.data);

export const unlockShot = (shotId, reason, by = 'cd') =>
  axios.post(`${API()}/shots/${shotId}/unlock-igk2p`, { by, reason }).then((r) => r.data);

export const getLockAudit = (shotId) =>
  axios.get(`${API()}/shots/${shotId}/lock-audit-igk2p`).then((r) => r.data);

export const confirmLineup = (projectId, sceneNumber) =>
  axios.post(`${API()}/projects/${projectId}/scenes/${sceneNumber}/confirm-lineup-igk2p`, {}).then((r) => r.data);

// Scene Look re-grade (apply = re-grade only, zero regeneration).
export const applySceneLook = (projectId, sceneNumber, lookTitle) =>
  axios.post(`${API()}/projects/${projectId}/scenes/${sceneNumber}/apply-look-igk2p`, { look_title: lookTitle }).then((r) => r.data);

// ── Derived state helpers ─────────────────────────────────────────────────
// Blueprint traffic-light state (DELTA-1): green=CD approved, amber=pending CD,
// red=redraw/stale/failed, grey=not generated.
// True only when a shot actually went through an IGK-2P run (blueprint drawn,
// 2P compose ran, or the shot is 2P-locked). Legacy / hyperreal-only shots
// return false → they render a PLAIN keyframe card (no 2P status bar, no bench,
// no scaffolding). Spec: Rohit 2026-07 keyframe-grid regression.
export function hasIgk2pRun(shot) {
  if (!shot) return false;
  const bp = shot.blueprint_status;
  const hasBlueprint = (!!bp && bp !== 'not_generated') || !!shot.blueprint_url;
  const hasCompose = !!shot.keyframe_igk2p_status;
  const locked = !!shot.igk2p_lock;
  return hasBlueprint || hasCompose || locked;
}

// Current graphite source URL, mirroring backend resolve_blueprint_url order
// (latest SAM iteration → sketch_s3_url → sketch_url → quick_sketch_url).
function currentGraphiteUrl(shot) {
  const sam = shot?.sketch_sam_iterations;
  if (Array.isArray(sam) && sam.length) {
    const u = (sam[sam.length - 1]?.image_url || '').trim();
    if (u) return u;
  }
  for (const f of ['sketch_s3_url', 'sketch_url', 'quick_sketch_url']) {
    const u = (shot?.[f] || '').trim();
    if (u) return u;
  }
  return '';
}

export function blueprintState(shot) {
  const status = shot?.blueprint_status || 'not_generated';
  const approved = !!shot?.blueprint_cd_approved;
  if (shot?.blueprint_stale || status === 'failed') return 'red';
  // Retroactive sketch-drift: a blueprint drawn from an older graphite than the
  // shot's CURRENT sketch is stale even when not explicitly flagged (e.g. it was
  // iterated before invalidation existed) → show Redraw.
  const srcUrl = (shot?.blueprint_source_graphite_url || '').trim();
  if (srcUrl && (status === 'ready' || shot?.blueprint_url)) {
    const cur = currentGraphiteUrl(shot);
    if (cur && cur !== srcUrl) return 'red';
  }
  if (status === 'ready' && approved) return 'green';
  if (status === 'ready') return 'amber';
  if (status === 'generating') return 'amber';
  return 'grey';
}

export const STATE_META = {
  green: { dot: 'bg-emerald-400', ring: 'ring-emerald-400/50', text: 'text-emerald-300', label: 'CD approved' },
  amber: { dot: 'bg-amber-400', ring: 'ring-amber-400/50', text: 'text-amber-300', label: 'Pending CD' },
  red: { dot: 'bg-red-500', ring: 'ring-red-500/50', text: 'text-red-300', label: 'Redraw / stale' },
  grey: { dot: 'bg-white/30', ring: 'ring-white/20', text: 'text-white/50', label: 'Not generated' },
};

// Scene→look mapping mirrors backend services/igk2p/scene_looks.py (read-only
// display; there is no scene-look HTTP endpoint yet — apply is morning-queued).
export const SCENE_LOOK_MAP = {
  1: 'AU_Daylight', 2: 'AU_Daylight', 3: 'AU_Daylight',
  4: 'AU_Dusk', 5: 'AU_Dusk',
  6: 'AU_Daylight', 7: 'AU_Daylight', 8: 'AU_Daylight', 9: 'AU_Daylight',
};

export function sceneInt(scene) {
  if (scene == null) return null;
  const m = String(scene).match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

export function resolveLook(scene) {
  const n = sceneInt(scene);
  return (n != null && SCENE_LOOK_MAP[n]) || 'AU_Daylight';
}
